import { useState, useEffect, useRef, useCallback } from 'react';
import { UserProfile } from '../types';

interface UseCollapsibleStateOptions {
  namespace: string; // e.g., 'mypage_groups', 'settings_groups', 'dashboard_widgets'
  defaultState: Record<string, boolean>;
  userProfile?: UserProfile | null;
  onUpdateProfile?: (updates: Partial<UserProfile>) => Promise<void> | void;
}

export function useCollapsibleState({
  namespace,
  defaultState,
  userProfile,
  onUpdateProfile,
}: UseCollapsibleStateOptions) {
  const localStorageKey = `optix_ui_collapsed_${namespace}`;

  // 1. Initial State Resolution Order: userProfile > localStorage > defaultState
  const getInitialState = (): Record<string, boolean> => {
    const serverSaved = userProfile?.uiCollapsedPreferences?.[namespace];
    if (serverSaved && typeof serverSaved === 'object') {
      return { ...defaultState, ...serverSaved };
    }
    try {
      const localSaved = localStorage.getItem(localStorageKey);
      if (localSaved) {
        return { ...defaultState, ...JSON.parse(localSaved) };
      }
    } catch (e) {
      // Fallback
    }
    return defaultState;
  };

  const [state, setState] = useState<Record<string, boolean>>(getInitialState);

  // Sync state when userProfile arrives or updates from server
  useEffect(() => {
    const serverSaved = userProfile?.uiCollapsedPreferences?.[namespace];
    if (serverSaved && typeof serverSaved === 'object') {
      setState(prev => {
        const merged = { ...defaultState, ...serverSaved };
        // Save to local storage as well for instant load on offline/reload
        try {
          localStorage.setItem(localStorageKey, JSON.stringify(merged));
        } catch (e) {}
        return merged;
      });
    }
  }, [userProfile?.uiCollapsedPreferences, namespace]);

  const saveTimerRef = useRef<NodeJS.Timeout | null>(null);

  const toggleSection = useCallback((key: string, explicitValue?: boolean) => {
    setState(prev => {
      const nextVal = explicitValue !== undefined ? explicitValue : !prev[key];
      const updated = { ...prev, [key]: nextVal };

      // Local storage instant update
      try {
        localStorage.setItem(localStorageKey, JSON.stringify(updated));
      } catch (e) {}

      // Server update with debounce to avoid rapid API calls during spam clicks
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        if (onUpdateProfile) {
          const currentPrefs = userProfile?.uiCollapsedPreferences || {};
          const nextPrefs = {
            ...currentPrefs,
            [namespace]: updated,
          };
          onUpdateProfile({ uiCollapsedPreferences: nextPrefs });
        }
      }, 500);

      return updated;
    });
  }, [namespace, localStorageKey, onUpdateProfile, userProfile?.uiCollapsedPreferences]);

  const isCollapsed = useCallback((key: string): boolean => {
    if (state[key] !== undefined) return state[key];
    return defaultState[key] ?? false;
  }, [state, defaultState]);

  return {
    state,
    isCollapsed,
    toggleSection,
  };
}
